import React from 'react';
import { CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import {
  getRAGStatus, getRAGCardClass, getRAGDotClass,
} from '../../config/dashboardHelpers';

const ExecutiveSummaryStrip = ({
  turnoverData,
  overtimeStats,
  restDayStats,
  drcRatio,
  drcRatioDisplay,
  complianceExpiryData,
}) => {
  const RAGIcon = ({ status }) => {
    if (status === 'green') return <CheckCircle className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden="true" />;
    if (status === 'amber') return <AlertTriangle className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden="true" />;
    if (status === 'red') return <AlertCircle className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden="true" />;
    return <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getRAGDotClass(status)}`} aria-hidden="true" />;
  };

  const workforceStatus = getRAGStatus('turnover', turnoverData?.rolling_12_month?.turnover_rate?.overall);
  const otExceeded = (overtimeStats?.exceededCount || 0) + (overtimeStats?.criticalCount || 0);
  const rdViolated = (restDayStats?.nonCompliantCount || 0) + (restDayStats?.criticalCount || 0);
  const complianceTotal = otExceeded + rdViolated;
  const complianceStatus = getRAGStatus('compliance', complianceTotal);
  const fwStatus = getRAGStatus('drc', drcRatio || 0);
  const complianceExpiryStatus = getRAGStatus('complianceExpiry', null, complianceExpiryData);
  const totalComplianceUrgent = complianceExpiryData
    ? (complianceExpiryData.work_pass?.action_required || 0)
      + (complianceExpiryData.passport?.action_required || 0)
      + (complianceExpiryData.security_bond?.action_required || 0)
      + (complianceExpiryData.safety_certs?.action_required || 0)
    : 0;

  const tiles = [
    {
      key: 'workforce',
      label: 'Workforce',
      status: workforceStatus,
      text: turnoverData?.rolling_12_month?.turnover_rate?.overall != null
        ? `${turnoverData.rolling_12_month.turnover_rate.overall.toFixed(1)}% turnover`
        : 'Loading...',
    },
    {
      key: 'compliance',
      label: 'MOM Compliance',
      status: complianceStatus,
      text: complianceTotal === 0 ? 'All clear' : `${complianceTotal} violation${complianceTotal > 1 ? 's' : ''}`,
    },
    {
      key: 'fw-quota',
      label: 'FW Quota',
      status: fwStatus,
      text: (drcRatio || 0) > 0 ? `DRC 1:${drcRatioDisplay}` : 'Loading...',
    },
    {
      key: 'passes',
      label: 'Compliance Expiry',
      status: complianceExpiryStatus,
      text: complianceExpiryData
        ? (totalComplianceUrgent === 0 ? 'All valid' : `${totalComplianceUrgent} action needed`)
        : 'Loading...',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6" role="region" aria-label="Executive health summary">
      {tiles.map((tile) => (
        <div key={tile.key} className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${getRAGCardClass(tile.status)}`}>
          <RAGIcon status={tile.status} />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{tile.label}</p>
            <p className="text-sm font-bold truncate">{tile.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ExecutiveSummaryStrip;
